import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import debouncePromise from 'debounce-promise';
import { ModalWithForm } from '../modal';
import { useShowToast } from '@/hooks';
import { AlreadyPartOf } from './components';
import { useMutation } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import {
  Avatar,
  Box,
  Flex,
  Link,
  Tooltip,
  UseDisclosureProps,
  Stack,
  Divider,
} from '@chakra-ui/react';
import { CustomOptions } from '@/ui-library/components/organisms/KFields/CustomOptions';
import { atsApplicationApi } from '@/store/query/ats-jobs';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { useGetContactByIdQuery } from '@/store/query/contacts';
import {
  SelectOptionWithAvatar,
  SelectWithDescriptionOption,
  CandidateSourceMenu,
  CandidateSourceSingleValue,
} from '@/components/formBuilder/SelectComponents';
import { useGetCandidateSourcesQuery } from '@/store/query/candidate-sources';
import { JobListMenu } from '../filters/components/UiItems/JobList';
import { KTag, KText } from '@/ui-library/components';
import { useParams } from 'react-router-dom';
import { TRANSFER_NOTE_STRINGS } from './TransferToJob.constants';

const errorMap = {
  err_cannot_transfer_to_same_job: 'Cannot transfer to the same job',
};

// AsyncSelect provides no static `options` prop, so JobListMenu would read `undefined`
// until the first loadOptions resolves. Default it to [] here (scoped to this picker)
// rather than changing the shared JobListMenu. Module-scoped so its identity is stable.
const TransferJobListMenu = (menuProps: any) => (
  <JobListMenu
    {...menuProps}
    selectProps={{
      ...menuProps.selectProps,
      options: menuProps.selectProps?.options || [],
    }}
  />
);

interface TransferCandidate {
  id?: number;
  kula_ats_candidate_id?: number;
  first_name?: string;
  last_name?: string;
  name?: string;
  image_url?: string;
}

interface TransferToJobProps {
  modalActions: UseDisclosureProps;
  candidate: TransferCandidate;
  applicationId: number;
  application: any;
  onSuccess: () => void;
}

export const TransferToJob = ({
  modalActions,
  candidate,
  applicationId,
  application,
  onSuccess = () => {},
}: TransferToJobProps) => {
  const [stageOptions, setStageOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const contactId = candidate?.id || candidate?.kula_ats_candidate_id;
  const { jobId } = useParams();
  const { ats_candidate_source = {}, credited_to = {} } = application || {};

  // Fetch contact details with existing applications
  const { data: contactData } = useGetContactByIdQuery(
    { contactId },
    { skip: !contactId || isLoading }
  );

  // Lazy trigger so the job dropdown loads options straight from the server per query
  // (see fetchJobOptions) instead of eagerly fetching a capped first page. This is the
  // ENG-3338 fix: the old `limit: 500` page dropped jobs ranked >500 (bukuwarung has
  // 1123 published/scheduled jobs), so the transfer target was unreachable.
  const [triggerJobSearch] = atsApplicationApi.useLazyGetAllJobsQuery();

  // Formik handle so the job field's onChange can reset the dependent stage field
  // (autoComplete's customOnChange isn't passed the formik bag, unlike kSelect's).
  const jobFormRef = useRef<any>(null);
  const { data: candidateSources } = useGetCandidateSourcesQuery(
    {},
    { skip: isLoading }
  );
  const availableSources = candidateSources?.filter(
    (item) => item?.enabled && !item?.deleted
  );
  const toast = useShowToast();
  const currUser = useSelector(selectCurrUser);
  const users = useSelector(selectUsers);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const usersOptions = users?.map((user) => ({
    ...user,
    label: user?.name,
    value: user?.id,
  }));

  // Get existing job IDs from contact's ats_applications (memoized so the loadOptions
  // closure below stays referentially stable across renders).
  const existingJobIds = useMemo(
    () => contactData?.ats_applications?.map((app) => app?.job_id) || [],
    [contactData]
  );

  // Map a raw job into the rich option the menu rows and stage dropdown rely on.
  const mapJobToOption = useCallback(
    (item) => {
      const isExistingJob = existingJobIds.includes(item?.id);
      return {
        value: item?.id,
        label: item?.name,
        status: item?.state,
        department: item?.department,
        locations: item?.offices || [],
        isDisabled: isExistingJob,
        disableReason: isExistingJob
          ? 'Candidate already added to this job'
          : undefined,
        ...item,
      };
    },
    [existingJobIds]
  );

  // loadOptions always queries the server (empty q -> base list on open, q text -> DB
  // search) so any published/scheduled job is reachable regardless of the total count.
  const fetchJobOptions = useCallback(
    async (input) => {
      try {
        const result = await triggerJobSearch({
          params: {
            view: 'interview_plan',
            scope: 'viewable',
            filter_by: { job_statuses: ['published', 'scheduled'] },
            sort_by: { open_at: 'DESC' },
            limit: 50,
            q: input || '',
          },
        }).unwrap();
        return (result || [])
          .filter((item) => item?.id !== Number(jobId))
          .map(mapJobToOption);
      } catch {
        return [];
      }
    },
    [triggerJobSearch, jobId, mapJobToOption]
  );

  // Debounce so typing doesn't fire a request per keystroke.
  const debouncedLoadJobOptions = useMemo(
    () => debouncePromise(fetchJobOptions, 300),
    [fetchJobOptions]
  );

  const switchLabelStyles = {
    fontWeight: 550,
    marginBottom: 0,
  };

  const labelStyles = {
    fontWeight: 550,
  };

  const renderHeaderWithCandidate = () => {
    const { first_name, last_name, image_url, name } = candidate || {};
    const candidateName = first_name
      ? `${first_name || ''} ${last_name || ''}`
      : name;
    return (
      <Flex alignItems="center" gap="6px">
        <KText size="sm-semi-bold" color="secondary-900">
          Transfer to another job:
        </KText>
        <Avatar
          size="xs"
          name={candidateName}
          src={image_url || ''}
          maxH="20px !important"
          w="20px !important"
          borderRadius="6px"
        />
        <KText
          size="sm-medium"
          maxW="150px"
          isTruncated={true}
          enableTooltip={true}
        >
          {candidateName}
        </KText>
      </Flex>
    );
  };

  const formConfig = [
    {
      name: 'transfer_note',
      type: 'custom',
      Field: () => {
        return (
          <KTag
            bg="pastel-6-light"
            color="pastel-6-dark"
            py="12px"
            border="1px solid"
            borderColor="#F7D29B"
            borderRadius="6px"
            px="0px !important"
            value={
              <Stack spacing="8px">
                <KText color="pastel-6-dark" size="sm-medium" px="12px">
                  {TRANSFER_NOTE_STRINGS.warning}
                </KText>
                <Divider borderColor="#F7D29B" />
                <Stack spacing="8px">
                  <KText color="pastel-6-dark" size="sm-regular" px="12px">
                    <KText
                      color="pastel-6-dark"
                      size="sm-medium"
                      display="inline-block"
                    >
                      {TRANSFER_NOTE_STRINGS.transferredLabel}{' '}
                    </KText>{' '}
                    {TRANSFER_NOTE_STRINGS.transferredItems}
                  </KText>
                  <KText color="pastel-6-dark" size="sm-regular" px="12px">
                    <KText
                      color="pastel-6-dark"
                      size="sm-medium"
                      display="inline-block"
                    >
                      {TRANSFER_NOTE_STRINGS.notTransferredLabel}{' '}
                    </KText>{' '}
                    {TRANSFER_NOTE_STRINGS.notTransferredItems}
                  </KText>
                </Stack>
              </Stack>
            }
          />
        );
      },
    },
    {
      name: 'job_id',
      label: 'Select job',
      type: 'autoComplete',
      labelStyles: labelStyles,
      isRequired: true,
      styleVersion: 'v2',
      placeholder: 'Select job',
      valueKey: 'value',
      labelKey: 'label',
      // AsyncSelect: base list on open (empty q), DB search-by-`q` on type. JobListMenu
      // still renders the rich rows; filterOption is disabled so the menu owns display
      // filtering.
      loadOptions: debouncedLoadJobOptions,
      cacheOptions: true,
      filterOption: () => true,
      showToggle: false,
      components: {
        Menu: TransferJobListMenu,
        IndicatorSeparator: () => null,
      },
      customOnChange: (value) => {
        setStageOptions(value?.stages || []);
        jobFormRef.current?.setFieldValue('stage_id', null, false);
        jobFormRef.current?.setFieldTouched('stage_id', false, false);
      },
    },
    {
      name: 'stage_id',
      label: 'Select stage',
      type: 'kSelect',
      isRequired: true,
      disabled: stageOptions.length === 0,
      placeholder: 'Select stage',
      labelStyles: labelStyles,
      styleVersion: 'v2',
      options: (() => {
        const hiredPosition =
          stageOptions.find((s) => s.name === 'Hired')?.position ?? Infinity;
        return stageOptions.map((stage) => {
          const isDisabled = (stage.position ?? 0) >= hiredPosition;
          return {
            ...stage,
            isDisabled,
            disableReason: isDisabled
              ? "Candidates can't be transferred directly to the Hired stage"
              : undefined,
          };
        });
      })(),
      valueKey: 'id',
      labelKey: 'name',
      components: {
        Option: (props) => {
          const { data } = props;
          const optionContent = <CustomOptions {...props} />;
          if (data?.isDisabled && data?.disableReason) {
            return (
              <Tooltip label={data.disableReason} hasArrow>
                <Box as="div" w="100%">
                  {optionContent}
                </Box>
              </Tooltip>
            );
          }
          return optionContent;
        },
      },
      styles: {
        menuList: (provided) => ({
          ...provided,
          padding: '0px',
          maxHeight: '150px',
        }),
      },
    },
    {
      name: 'source_credits',
      label: 'Change source and credited to',
      type: 'switch',
      labelStyles: switchLabelStyles,
      initialValue: currUser.id,
      style: {
        // Group the switch next to its label (override SwitchField's
        // space-between) and add the gap between them.
        flexDirection: 'row-reverse',
        justifyContent: 'flex-end',
        gap: '8px',
      },
      isNewFilter: true,
    },
    {
      isConditional: true,
      conditionConfig: { field: 'source_credits', value: true },
      name: 'ats_candidate_source_id',
      label: 'Select source',
      placeholder: 'Select source',
      type: 'kSelect',
      labelStyles: labelStyles,
      options: availableSources,
      valueKey: 'id',
      isMulti: false,
      labelKey: 'name',
      components: {
        Menu: CandidateSourceMenu,
        SingleValue: CandidateSourceSingleValue,
        IndicatorSeparator: () => null,
      },
      styles: {
        menu: (base) => ({
          ...base,
          padding: 0,
        }),
        menuList: (provided) => ({
          ...provided,
          padding: '0px',
          maxHeight: '200px', // Control max height of the dropdown
        }),
      },
    },
    {
      isConditional: true,
      conditionConfig: { field: 'source_credits', value: true },
      name: 'credited_to_user_id',
      label: 'Select credited to',
      placeholder: 'Select credited to',
      type: 'kSelect',
      labelStyles: labelStyles,
      isMulti: false,
      width: '100%',
      customFormatLabel: SelectOptionWithAvatar,
      components: {
        Option: SelectWithDescriptionOption,
      },
      menuPlacement: 'top',
      showAvatar: true,
      descKey: 'email',
      avatarKey: 'avatar',
      isNearLabel: true,
      options: usersOptions,
      valueKey: 'id',
    },
  ];

  interface TransferPayload {
    target_job_id?: number;
    target_stage_id?: number;
    candidate_source_id?: number;
    credited_to_id?: number;
  }

  const transferMutation = useMutation({
    mutationFn: async (payload: TransferPayload) => {
      const response = await http.post(
        `/ats_applications/${applicationId}/transfer`,
        payload
      );
      // @ts-expect-error http post response shape includes errors array
      if (response?.errors && response?.errors.length > 0) {
        throw {
          response: {
            // @ts-expect-error errors array mapped to reason shape
            errors: response?.errors.map((err: string) => ({
              reason: err,
            })),
          },
        };
      }

      return response;
    },
    onSuccess: (data: unknown, variables: TransferPayload) => {
      const targetJobId = variables?.target_job_id;
      const body =
        data && typeof data === 'object' && 'data' in data
          ? (data as { data?: { id?: number } }).data
          : (data as { id?: number });
      const newAppId = body?.id;
      const viewUrl =
        targetJobId !== undefined && targetJobId !== null
          ? `/jobs/${targetJobId}?${newAppId !== undefined && newAppId !== null ? `appId=${newAppId}&` : ''}currentView=stage&tab=candidates`
          : null;

      const title: React.ReactNode =
        viewUrl === null ? (
          'Application transferred successfully'
        ) : (
          <>
            Application transferred successfully.{' '}
            <Link
              as="button"
              type="button"
              onClick={() => window.open(viewUrl, '_self')}
              textDecoration="underline"
              fontWeight="600"
              cursor="pointer"
              _hover={{ opacity: 0.9 }}
              data-test-id="toast-view-candidate-link"
              _active={{
                border: 'none',
              }}
            >
              View candidate
            </Link>
          </>
        );
      toast({ title, duration: 10000 });
      modalActions?.onClose();
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const err = error as {
        response?: { error?: unknown; errors?: unknown[] };
      };
      const errorData = err?.response?.error || err?.response?.errors;

      const errorMsg = errorMap[errorData?.[0]];
      toast({
        title: `Unable to transfer application`,
        description: errorMsg,
        status: 'error',
      });
    },
  });

  const handleSubmit = async (values) => {
    const { job_id, stage_id } = values;

    const payload = {
      target_job_id: job_id,
      target_stage_id: stage_id,
      ...(values?.ats_candidate_source_id && {
        candidate_source_id: values?.ats_candidate_source_id,
      }),
      ...(values?.credited_to_user_id &&
        values?.source_credits && {
          credited_to_id: values?.credited_to_user_id,
        }),
    };

    transferMutation.mutate(payload);
  };

  return (
    <>
      <ModalWithForm
        isOpen={modalActions?.isOpen}
        onClose={modalActions?.onClose}
        formConfig={formConfig}
        customFormRef={jobFormRef}
        primaryCtaText="Transfer"
        isCtaLoading={transferMutation.isPending}
        headerText={renderHeaderWithCandidate()}
        handleSubmit={handleSubmit}
        formContainerStyles={{ minHeight: '250px' }}
        showAdditionalBlockAfter={true}
        AdditionalBlockAfter={AlreadyPartOf}
        initialValues={{
          credited_to_user_id: credited_to?.id,
          ats_candidate_source_id:
            availableSources?.find(
              (source) => source?.id === ats_candidate_source?.id
            )?.id || undefined,
        }}
        modalHeaderProps={{
          borderBottom: '1px solid',
          borderBottomColor: 'border-color',
        }}
        modalFooterProps={{
          borderTop: '1px solid',
          borderTopColor: 'border-color',
          p: '12px 24px !important',
        }}
        modalBodyProps={{
          py: '12px',
        }}
        showCloseButton={false}
      />
    </>
  );
};
